/*       */

import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';

import { bind } from 'app/utils/decorators/decoratorUtils';
import EntityLayerAbout from 'app/containers/Maps/LayerAbout/EntityLayer/EntityLayerAbout';
import WMSLayerAbout from 'app/containers/Maps/LayerAbout/WMSLayer/WMSLayerAbout';
import DrawingLayerAbout from 'app/containers/Maps/LayerAbout/DrawingLayer/DrawingLayerAbout';
import { Button } from '@mic3/platform-ui';
import { setActions, setOnBack, setTitle } from 'store/actions/sidebar/sidebarActions';

import DotMenu from 'app/components/molecules/DotMenu/DotMenu';
import RemoveLayerModal from 'app/containers/Maps/Drawers/RemoveLayerModal';
import BaseModal from 'app/components/Designer/Modals/BaseModal';
import FormGenerator from 'app/containers/Designer/Form/components/FormGenerator';
import CesiumIonLayerAbout from 'app/containers/Maps/LayerAbout/CesiumIonLayerAbout/CesiumIonLayerAbout';
import { getPermissions } from 'app/config/rolesConfig';
import MapSidebar from 'app/containers/Sidebars/MapSidebar';
import { layerTypes, __findMapLayer, getLayerNameField, __getMapLayers } from 'app/utils/maps/layer/layerUtils';
import { updateMapLayer } from 'store/actions/maps/situationalAwarenessActions';
import { get } from 'app/utils/lo/lo';
import ReplayLayerAbout from 'app/containers/Maps/LayerAbout/ReplayLayer/ReplayLayerAbout'; 
import { deleteRelationship } from 'store/actions/entities/relationshipsActions';
import XYZLayerAbout from 'app/containers/Maps/LayerAbout/XYZLayer/XYZLayerAbout';

class LayerSidebar extends PureComponent {
    static propTypes = {
        title: PropTypes.string,
        id: PropTypes.string.isRequired,
        map: PropTypes.object.isRequired,
        mapId: PropTypes.string.isRequired,
        layer: PropTypes.object.isRequired,
        details: PropTypes.object,
        isOpen: PropTypes.bool,
    };

    renameRef = React.createRef();

    constructor(props) {
        super(props);
        this.state = {
            onSave: null,
            isRename: false,
            layer: null,
            isChanged: false,
        };
        this.setOnBack();
    }

    componentDidMount() {
        this.setActions();
    }

    componentDidUpdate(prevProps) {
        const { title, mapData, map } = this.props;
        
        if (title !== prevProps.title && title !== 'Layers') {
            this.setOnBack();
        }
        
        this.handleReplayLayerUpdate(mapData, prevProps.mapData, map);
    }
    
    handleReplayLayerUpdate(currentMapData, prevMapData, map) {
        const { id } = this.props; 
        
        const currentReplayLayer = currentMapData?.primary?.['system_map/layers']?.find(
            layer => layer.id === id
        );
        const prevReplayLayer = prevMapData?.primary?.['system_map/layers']?.find(
            layer => layer.id === id
        );
        
        if (!currentReplayLayer) {
            return; 
        }
        
        const hasDescriptionChanged = currentReplayLayer.description !== prevReplayLayer?.description;

        const hasTimeRangeChanged = !this.areTimeRangesEqual(
            currentReplayLayer.time_range, 
            prevReplayLayer?.time_range
        );
        
        const hasRelatedEntitiesChanged = !this.areRelatedEntitiesEqual(
            currentReplayLayer.relatedEntities, 
            prevReplayLayer?.relatedEntities
        );

        // TODO maybe realted to the related entities not updated
        // Only update if time_range or relatedEntities have changed
        if (hasDescriptionChanged || hasTimeRangeChanged || hasRelatedEntitiesChanged) {
            const replayLayerInstance = map.entityLayers[currentReplayLayer.id];
            
            if (replayLayerInstance) {
                replayLayerInstance.updateInfo(currentReplayLayer);
                
                replayLayerInstance.setLayer();
            }
        }
    }
    
    areTimeRangesEqual(timeRange1, timeRange2) {
        if (!timeRange1 && !timeRange2) return true;
        if (!timeRange1 || !timeRange2) return false;
        
        // Handle relative time format
        if (timeRange1.relative && timeRange2.relative) {
            return (
                timeRange1.amount === timeRange2.amount &&
                timeRange1.unit === timeRange2.unit &&
                timeRange1.range === timeRange2.range
            );
        }
        
        // Handle array format with date objects or ISO strings
        if (Array.isArray(timeRange1) && Array.isArray(timeRange2)) {
            // Convert to timestamps for comparison if they're Date objects
            const start1 = timeRange1[0] instanceof Date ? timeRange1[0].getTime() : new Date(timeRange1[0]).getTime();
            const start2 = timeRange2[0] instanceof Date ? timeRange2[0].getTime() : new Date(timeRange2[0]).getTime();
            const end1 = timeRange1[1] instanceof Date ? timeRange1[1].getTime() : new Date(timeRange1[1]).getTime();
            const end2 = timeRange2[1] instanceof Date ? timeRange2[1].getTime() : new Date(timeRange2[1]).getTime();
            
            return start1 === start2 && end1 === end2;
        }
        
        // Handle object format with numeric indices for timestamps
        if (timeRange1[0] && timeRange2[0] && timeRange1[1] && timeRange2[1]) {
            const start1 = new Date(timeRange1[0]).getTime();
            const start2 = new Date(timeRange2[0]).getTime();
            const end1 = new Date(timeRange1[1]).getTime();
            const end2 = new Date(timeRange2[1]).getTime();
            
            return start1 === start2 && end1 === end2;
        }
        
        return false;
    }
    
    areRelatedEntitiesEqual(entities1, entities2) {
        if (!entities1 && !entities2) return true;
        if (!entities1 || !entities2) return false;
        if (entities1.length !== entities2.length) return false;
        
        const entitiesMap1 = {};
        const entitiesMap2 = {};
        
        entities1.forEach(entity => {
            entitiesMap1[entity.id] = entity;
        });
        
        entities2.forEach(entity => {
            entitiesMap2[entity.id] = entity;
        });
        
        for (const entityId in entitiesMap1) {
            if (!entitiesMap2[entityId]) {
                return false;
            }
        }
        
        for (const entityId in entitiesMap2) {
            if (!entitiesMap1[entityId]) {
                return false;
            }
        }
        
        return true;
    }

    @bind
    onLayerRemove() {
        const { id, title } = this.props;
        this.openModal({ id, title });
    }

    @bind
    renderLayerActionMenu() {
        const moreMenu = [
            { name: 'Rename', icon: 'pencil', onItemClick: () => this.setState({ isRenameModelOpen: true }) },
            { name: 'Delete', icon: 'delete', onItemClick: this.onLayerRemove },
        ];
        return <DotMenu items={moreMenu} />;
    }

    @bind
    setStatus(value, callback) {
        return this.setState({ isChanged: value }, () => callback && callback());
    }

    @bind
    setOnBack() {
        this.props.setOnBack(() => {
            this.props.setTitle('Layers');
        });
    }

    @bind
    setActions(onSave) {
        const { mapUserRole } = this.props || {};
        const { canEdit } = getPermissions(mapUserRole);

        if (!canEdit) {
            return;
        }

        if (this.state.onSave !== onSave) { 
            this.setState({ onSave });
        }

        return this.props.setActions(
            <>
                <Button
                    style={{ marginTop: '4px' }}
                    disabled={!onSave || !this.state.isChanged}
                    variant="text"
                    onClick={async () => {
                        typeof onSave === 'function' && (await onSave());
                        this.setState({ onSave: null });
                    }}
                >
                    Apply
                </Button>
                {this.renderLayerActionMenu()}
            </>
        );
    }

    @bind
    onCancel() {
        this.setState({ isChanged: false });
        if (this.state.goToLayers) {
            this.props.setTitle('Layers');
        }
    }

    @bind
    async onSave() {
        const { onSave } = this.state;
        if (typeof onSave === 'function') {
            await onSave();
        }
        this.onCancel();
    }

    @bind
    renderContent() {
        const { title, id, layer, map, mapId, entityTypes, mapData, data, layerName } = this.props;
        const { isChanged } = this.state;
        const layerDetails = __findMapLayer(mapData, { id, name: title });
        const layerType = 'entity-layer';
        const layerTitle = layerTypes.find(lyr => lyr.uri === layerDetails?.type)?.name || '';
        const props = {
            layerName,
            id,
            layer,
            map,
            mapId,
            title: layerTitle,
            setActions: this.setActions,
            entityTypes,
            setStatus: this.setStatus,
            isChanged,
            details: data || layerDetails || {},
        };

        switch (title || layerTitle) {
            case 'Layers':
                return <MapSidebar map={map} id={mapId} mapId={mapId} type="map" title={title} layerName={layerName} />;
            case 'Entity Layer':
            case 'Event Layer':
                return <EntityLayerAbout {...props} type={layerType} />;
            case 'WMS Layer':
                return <WMSLayerAbout {...props} />;
            case 'Drawing Layer':
                return <DrawingLayerAbout {...props} />;
            case 'Cesium Ion Layer':
                return <CesiumIonLayerAbout {...props} />;
            case 'Replay Layer':
                return <ReplayLayerAbout {...props} />;
            case 'XYZ Tile Layer':
                return <XYZLayerAbout {...props} />;
            default:
                return null;
        }
    }

    @bind
    openModal(layer) {
        this.setState({ layer });
    }

    @bind
    closeModal() {
        this.setState({ layer: null });
    }

    @bind
    async handleConfirmRename() {
        const result = await this.renameRef.current.isValidForm();
        if (!result.errors) {
            const { data } = result;
            const { id, updateMapLayer } = this.props;
            updateMapLayer({ id, name: data?.name });
            this.setState({ isRenameModelOpen: false });
        }
    }

    render() {
        const { isOpen, map, mapId, mapData, layerName, id, title } = this.props;
        const layerDetails = __findMapLayer(mapData, { id, name: this.props.layer?.values_?.title || layerName, title });
        const { isRenameModelOpen, layer } = this.state;
        const layersList = __getMapLayers(mapData)
        
        return (
            isOpen && (
                <>
                    {isRenameModelOpen && (
                        <BaseModal
                            open={isRenameModelOpen}
                            onClose={() => this.setState({ isRenameModelOpen: false })}
                            onConfirm={this.handleConfirmRename}
                            header="Rename Layer"
                            maxWidth="sm"
                            fullWidth
                            content={<FormGenerator root={false} data={layerDetails} components={[getLayerNameField(layersList)]} ref={this.renameRef} />}
                            declineButtonText="Cancel"
                            confirmButtonText="Apply"
                        />
                    )}
                    {this.renderContent()}
                    {layer && 
                        <RemoveLayerModal
                            map={map}
                            mapId={mapId}
                            layer={layerDetails}
                            closeModal={this.closeModal}
                            deleteRelationship={this.props.deleteRelationship} 
                        />}
                </>
            )
        );
    }
}

export default connect(
    (state, ownProps) => ({
        isOpen: state.sidebar.isOpen,
        title: state.sidebar.title,
        isLoading: state.maps.situationalAwareness.map.data.isLoading,
        entityTypes: state.app.allPrimaryClasses.records,
        mapData: get(state.maps,'situationalAwareness.map.data',{}),
        mapData: get(state.maps,'situationalAwareness.map.data',{}),
        mapUserRole: get(state.maps, 'situationalAwareness.map.data.role', '')
    }),
    {
        setActions,
        setOnBack,
        setTitle,
        updateMapLayer,
        deleteRelationship
    }
)(withRouter(LayerSidebar));

